import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { ArrowLeft, ArrowRight, Heart, Share2, Download, ChevronLeft, ChevronRight, X, Maximize2 } from 'lucide-react';

// Design inspiration data
const inspirationData = {
  'modern-minimalist': {
    title: 'Modern Minimalist Living',
    description: 'Clean lines and functional beauty define the modern minimalist aesthetic. This design philosophy emphasizes simplicity, open spaces, and a neutral color palette to create serene living environments.',
    category: 'Living Room',
    images: [
      'https://images.unsplash.com/photo-1705321963943-de94bb3f0dd3?q=85&w=1200',
      'https://images.unsplash.com/photo-1600210492486-724fe5c67fb0?q=85&w=1200',
      'https://images.unsplash.com/photo-1617325247661-675ab4b64ae2?q=85&w=1200',
      'https://images.unsplash.com/photo-1600607687939-ce8a6c25118c?q=85&w=1200'
    ],
    features: ['Neutral color palette', 'Clean lines', 'Functional furniture', 'Open floor plan', 'Natural light emphasis'],
    tips: [
      'Choose furniture with simple geometric shapes',
      'Stick to a monochromatic color scheme with subtle accents',
      'Maximize natural light with sheer curtains',
      'Use hidden storage to maintain clutter-free spaces',
      'Incorporate plants for a touch of organic warmth'
    ]
  },
  'luxury-bedroom': {
    title: 'Luxury Bedroom Retreats',
    description: 'Transform your bedroom into a luxurious sanctuary. These designs combine comfort with elegance, featuring plush textiles, sophisticated color schemes, and thoughtful lighting.',
    category: 'Bedroom',
    images: [
      'https://images.unsplash.com/photo-1616594039964-ae9021a400a0?q=85&w=1200',
      'https://images.unsplash.com/photo-1618773928121-c32242e63f39?q=85&w=1200',
      'https://images.unsplash.com/photo-1560185007-cde436f6a4d0?q=85&w=1200',
      'https://images.unsplash.com/photo-1505693416388-ac5ce068fe85?q=85&w=1200'
    ],
    features: ['Plush bedding', 'Ambient lighting', 'Rich textures', 'Statement headboard', 'Layered textiles'],
    tips: [
      'Invest in high-quality bedding with high thread count',
      'Layer different textures for depth and warmth',
      'Install dimmable lighting for ambiance control',
      'Add a seating area for a true retreat feel',
      'Use blackout curtains for better sleep quality'
    ]
  },
  'contemporary-kitchen': {
    title: 'Contemporary Kitchens',
    description: 'Where function meets style. Contemporary kitchens feature sleek cabinetry, smart storage solutions, and premium appliances that make cooking a pleasure.',
    category: 'Kitchen',
    images: [
      'https://images.unsplash.com/photo-1556911220-bff31c812dba?q=85&w=1200',
      'https://images.unsplash.com/photo-1556909114-f6e7ad7d3136?q=85&w=1200',
      'https://images.unsplash.com/photo-1600489000022-c2086d79f9d4?q=85&w=1200',
      'https://images.unsplash.com/photo-1556909172-54557c7e4fb7?q=85&w=1200'
    ],
    features: ['Handleless cabinetry', 'Island workspace', 'Premium appliances', 'Smart storage', 'Under-cabinet lighting'],
    tips: [
      'Opt for handleless cabinets for a seamless look',
      'Include a kitchen island for extra workspace',
      'Install task lighting under cabinets',
      'Choose durable, easy-to-clean countertops',
      'Incorporate pull-out organizers for efficiency'
    ]
  },
  'zen-bathroom': {
    title: 'Zen Bathrooms',
    description: 'Create a spa-like atmosphere in your home. Zen bathrooms focus on tranquility with natural materials, soft lighting, and minimalist fixtures.',
    category: 'Bathroom',
    images: [
      'https://images.unsplash.com/photo-1552321554-5fefe8c9ef14?q=85&w=1200',
      'https://images.unsplash.com/photo-1600566752355-35792bedcfea?q=85&w=1200',
      'https://images.unsplash.com/photo-1507652313519-d4e9174996dd?q=85&w=1200',
      'https://images.unsplash.com/photo-1584622650111-993a426fbf0a?q=85&w=1200'
    ],
    features: ['Natural stone', 'Freestanding tub', 'Rainfall shower', 'Wooden accents', 'Soft lighting'],
    tips: [
      'Use natural materials like stone and wood',
      'Install a rainfall showerhead for a spa feel',
      'Add live plants for freshness',
      'Choose soft, warm lighting',
      'Include plenty of storage to maintain serenity'
    ]
  },
  'home-office': {
    title: 'Home Office Spaces',
    description: 'Productivity meets style in these home office designs. Create a workspace that inspires creativity while maintaining comfort and functionality.',
    category: 'Office',
    images: [
      'https://images.unsplash.com/photo-1484154218962-a197022b5858?q=85&w=1200',
      'https://images.unsplash.com/photo-1618221195710-dd6b41faaea6?q=85&w=1200',
      'https://images.unsplash.com/photo-1593642632559-0c6d3fc62b89?q=85&w=1200',
      'https://images.unsplash.com/photo-1600494603989-9650cf6ddd3d?q=85&w=1200'
    ],
    features: ['Ergonomic furniture', 'Good lighting', 'Cable management', 'Storage solutions', 'Personal touches'],
    tips: [
      'Invest in an ergonomic chair and desk',
      'Position desk near natural light',
      'Use cable management solutions',
      'Add personal items for motivation',
      'Include a comfortable reading corner'
    ]
  },
  'outdoor-living': {
    title: 'Outdoor Living',
    description: 'Extend your living space outdoors with these inspiring patio and garden designs. Perfect for entertaining or relaxing in nature.',
    category: 'Outdoor',
    images: [
      'https://images.unsplash.com/photo-1600585154340-be6161a56a0c?q=85&w=1200',
      'https://images.unsplash.com/photo-1600566753376-12c8ab7fb75b?q=85&w=1200',
      'https://images.unsplash.com/photo-1600573472591-ee6981cf35b6?q=85&w=1200',
      'https://images.unsplash.com/photo-1558618666-fcd25c85cd64?q=85&w=1200'
    ],
    features: ['Comfortable seating', 'Outdoor lighting', 'Green landscaping', 'Weather-resistant materials', 'Entertainment zone'],
    tips: [
      'Choose weather-resistant furniture',
      'Add string lights for ambiance',
      'Include shade options like pergolas',
      'Create zones for dining and lounging',
      'Incorporate potted plants for greenery'
    ]
  }
};

const slugToKey = (slug) => {
  const mapping = {
    'modern-minimalist': 'modern-minimalist',
    'luxury-bedroom': 'luxury-bedroom',
    'contemporary-kitchen': 'contemporary-kitchen',
    'zen-bathroom': 'zen-bathroom',
    'home-office': 'home-office',
    'outdoor-living': 'outdoor-living'
  };
  return mapping[slug] || slug;
};

const InspirationDetail = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [liked, setLiked] = useState(false);

  const key = slugToKey(slug);
  const inspiration = inspirationData[key];

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [slug]);

  if (!inspiration) {
    return (
      <div className="min-h-screen bg-white py-32 text-center" data-testid="inspiration-not-found">
        <h1 className="text-2xl font-bold mb-4">Design not found</h1>
        <Button onClick={() => navigate('/inspiration')}>
          <ArrowLeft className="w-4 h-4 mr-2" /> Back to Inspiration
        </Button>
      </div>
    );
  }

  const nextImage = () => {
    setCurrentImageIndex((prev) => (prev + 1) % inspiration.images.length);
  };

  const prevImage = () => {
    setCurrentImageIndex((prev) => (prev - 1 + inspiration.images.length) % inspiration.images.length);
  };

  const handleShare = async () => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: inspiration.title,
          text: inspiration.description,
          url: window.location.href,
        });
      } catch (err) {
        console.log('Share cancelled');
      }
    } else {
      navigator.clipboard.writeText(window.location.href);
      alert('Link copied to clipboard!');
    }
  };

  return (
    <div className="min-h-screen bg-white py-24" data-testid="inspiration-detail-page">
      {/* Back Button */}
      <div className="max-w-7xl mx-auto px-6 mb-6">
        <Button 
          variant="ghost" 
          onClick={() => navigate('/inspiration')}
          className="text-gray-600 hover:text-gray-900"
          data-testid="back-to-inspiration"
        >
          <ArrowLeft className="w-4 h-4 mr-2" /> Back to Inspiration
        </Button>
      </div>

      <div className="max-w-7xl mx-auto px-6">
        <div className="grid lg:grid-cols-2 gap-12">
          {/* Image Gallery */}
          <div>
            <div className="relative aspect-[4/3] rounded-2xl overflow-hidden group">
              <img
loading="lazy" decoding="async"                 src={inspiration.images[currentImageIndex]}
                alt={`${inspiration.title} - Image ${currentImageIndex + 1}`}
                className="w-full h-full object-cover"
              />
              
              {/* Navigation Arrows */}
              <button
                onClick={prevImage}
                className="absolute left-4 top-1/2 -translate-y-1/2 w-10 h-10 bg-white/90 rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity shadow-lg"
                data-testid="prev-image-btn"
              >
                <ChevronLeft className="w-6 h-6" />
              </button>
              <button
                onClick={nextImage}
                className="absolute right-4 top-1/2 -translate-y-1/2 w-10 h-10 bg-white/90 rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity shadow-lg"
                data-testid="next-image-btn"
              >
                <ChevronRight className="w-6 h-6" />
              </button>

              {/* Fullscreen Button */}
              <button
                onClick={() => setLightboxOpen(true)}
                className="absolute top-4 right-4 w-10 h-10 bg-white/90 rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity shadow-lg"
                data-testid="fullscreen-btn"
              >
                <Maximize2 className="w-5 h-5" />
              </button>

              {/* Image Counter */}
              <div className="absolute bottom-4 left-1/2 -translate-x-1/2 px-3 py-1 bg-black/60 text-white text-sm rounded-full">
                {currentImageIndex + 1} / {inspiration.images.length}
              </div>
            </div>

            {/* Thumbnails */}
            <div className="flex gap-3 mt-4 overflow-x-auto pb-2">
              {inspiration.images.map((img, idx) => (
                <button
                  key={idx}
                  onClick={() => setCurrentImageIndex(idx)}
                  className={`flex-shrink-0 w-20 h-20 rounded-lg overflow-hidden border-2 transition-all ${
                    idx === currentImageIndex ? 'border-primary' : 'border-transparent opacity-70 hover:opacity-100'
                  }`}
                  data-testid={`thumbnail-${idx}`}
                >
                  <img src={img} alt={`Thumbnail ${idx + 1}`} loading="lazy" decoding="async" className="w-full h-full object-cover" />
                </button>
              ))}
            </div>
          </div>

          {/* Content */}
          <div>
            <div className="flex items-center gap-3 mb-4">
              <span className="px-3 py-1 bg-[#F5F5F0] text-primary text-sm font-medium rounded-full">
                {inspiration.category}
              </span>
            </div>

            <h1 className="text-4xl font-bold text-gray-900 mb-4" data-testid="inspiration-detail-title">
              {inspiration.title}
            </h1>

            <p className="text-lg text-gray-600 mb-8">
              {inspiration.description}
            </p>

            {/* Action Buttons */}
            <div className="flex gap-3 mb-8">
              <Button
                variant="outline"
                onClick={() => setLiked(!liked)}
                className={liked ? 'text-red-500 border-red-500' : ''}
                data-testid="like-btn"
              >
                <Heart className={`w-5 h-5 mr-2 ${liked ? 'fill-current' : ''}`} />
                {liked ? 'Liked' : 'Like'}
              </Button>
              <Button variant="outline" onClick={handleShare} data-testid="share-btn">
                <Share2 className="w-5 h-5 mr-2" />
                Share
              </Button>
            </div>

            {/* Features */}
            <div className="mb-8">
              <h3 className="text-lg font-semibold mb-4">Key Features</h3>
              <div className="flex flex-wrap gap-2">
                {inspiration.features.map((feature, idx) => (
                  <span
                    key={idx}
                    className="px-4 py-2 bg-gray-100 text-gray-700 rounded-full text-sm"
                  >
                    {feature}
                  </span>
                ))}
              </div>
            </div>

            {/* Design Tips */}
            <div className="bg-[#F5F5F0] rounded-xl p-6">
              <h3 className="text-lg font-semibold mb-4">Design Tips</h3>
              <ul className="space-y-3">
                {inspiration.tips.map((tip, idx) => (
                  <li key={idx} className="flex items-start gap-3">
                    <span className="w-6 h-6 bg-primary text-white rounded-full flex items-center justify-center flex-shrink-0 text-sm">
                      {idx + 1}
                    </span>
                    <span className="text-gray-700">{tip}</span>
                  </li>
                ))}
              </ul>
            </div>

            {/* CTA */}
            <div className="mt-8">
              <Button
                size="lg"
                className="w-full bg-primary py-6 text-lg"
                onClick={() => navigate('/interior-design')}
                data-testid="get-quote-btn"
              >
                Get a Quote for This Style
                <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
            </div>
          </div>
        </div>

        {/* Related Designs */}
        <div className="mt-20">
          <h2 className="text-2xl font-bold mb-8">More Design Ideas</h2>
          <div className="grid md:grid-cols-3 gap-6">
            {Object.entries(inspirationData)
              .filter(([k]) => k !== key)
              .slice(0, 3)
              .map(([k, item]) => (
                <Link
                  key={k}
                  to={`/inspiration/${k}`}
                  className="group cursor-pointer"
                  data-testid={`related-${k}`}
                >
                  <div className="relative overflow-hidden rounded-xl h-64">
                    <img
loading="lazy" decoding="async"                       src={item.images[0]}
                      alt={item.title}
                      className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                    />
                    <div className="absolute inset-0 bg-black/40 group-hover:bg-black/60 transition-all duration-300" />
                    <div className="absolute bottom-0 left-0 right-0 p-6 text-white">
                      <p className="text-xs uppercase tracking-wider text-gray-300 mb-1">{item.category}</p>
                      <h3 className="text-xl font-bold">{item.title}</h3>
                    </div>
                  </div>
                </Link>
              ))}
          </div>
        </div>
      </div>

      {/* Lightbox */}
      {lightboxOpen && (
        <div 
          className="fixed inset-0 bg-black z-50 flex items-center justify-center"
          onClick={() => setLightboxOpen(false)}
        >
          <button
            onClick={() => setLightboxOpen(false)}
            className="absolute top-4 right-4 w-12 h-12 bg-white/20 rounded-full flex items-center justify-center text-white hover:bg-white/30 transition-colors"
          >
            <X className="w-6 h-6" />
          </button>
          <button
            onClick={(e) => { e.stopPropagation(); prevImage(); }}
            className="absolute left-4 top-1/2 -translate-y-1/2 w-12 h-12 bg-white/20 rounded-full flex items-center justify-center text-white hover:bg-white/30 transition-colors"
          >
            <ChevronLeft className="w-8 h-8" />
          </button>
          <button
            onClick={(e) => { e.stopPropagation(); nextImage(); }}
            className="absolute right-4 top-1/2 -translate-y-1/2 w-12 h-12 bg-white/20 rounded-full flex items-center justify-center text-white hover:bg-white/30 transition-colors"
          >
            <ChevronRight className="w-8 h-8" />
          </button>
          <img
loading="lazy" decoding="async"             src={inspiration.images[currentImageIndex]}
            alt={inspiration.title}
            className="max-w-full max-h-full object-contain"
            onClick={(e) => e.stopPropagation()}
          />
        </div>
      )}
    </div>
  );
};

export default InspirationDetail;
